import { useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import moment from "moment";
import {
  useApplyOpeningBalanceCorrectionMutation,
  useGetOpeningBalanceCorrectionFormQuery,
  useListStaffOpeningBalanceCorrectionsQuery,
} from "../../store/admin/memberManagement/memberSlice";
import { formatMoney } from "../../utils/currencyFormat";

/**
 * Staff-only: set target savings balance per category (replacement semantics).
 */
export default function OpeningBalanceCorrectionModal({
  show,
  onHide,
  numericMemberId,
  memberLabel = "",
}) {
  const [cutoverDate, setCutoverDate] = useState("");
  const [targets, setTargets] = useState({});

  const { data: formData, isFetching: formLoading } =
    useGetOpeningBalanceCorrectionFormQuery(numericMemberId, {
      skip: !show || !numericMemberId,
    });

  const { data: auditsData, isFetching: auditsLoading } =
    useListStaffOpeningBalanceCorrectionsQuery(
      { memberId: numericMemberId, page: 1, limit: 10 },
      { skip: !show || !numericMemberId },
    );

  const [applyCorrection, { isLoading: applying }] =
    useApplyOpeningBalanceCorrectionMutation();

  const categories = formData?.categories ?? [];

  /** API wraps Laravel pagination: data.data = paginator, rows = paginator.data */
  const correctionRows = useMemo(() => {
    const outer = auditsData?.data;
    if (!outer) return [];
    if (Array.isArray(outer)) return outer;
    if (Array.isArray(outer?.data)) return outer.data;
    return [];
  }, [auditsData]);

  useEffect(() => {
    if (!show) return;
    const today = new Date().toISOString().slice(0, 10);
    setCutoverDate(today);
  }, [show]);

  useEffect(() => {
    if (!categories.length) return;
    const init = {};
    categories.forEach((c) => {
      const id = c.contribution_category_id;
      init[id] = String(c.current_total ?? "0");
    });
    setTargets(init);
  }, [categories]);

  const balancesPayload = useMemo(() => {
    return categories.map((c) => ({
      contribution_category_id: c.contribution_category_id,
      target_total: targets[c.contribution_category_id] ?? "",
    }));
  }, [categories, targets]);

  const formatPreviewDelta = (categoryRow) => {
    const id = categoryRow.contribution_category_id;
    const current = Number(categoryRow.current_total ?? 0);
    const raw = targets[id];
    if (raw === "" || raw === undefined || raw === null) {
      return { text: "—", tone: "muted" };
    }
    const desired = Number(raw);
    if (!Number.isFinite(desired)) {
      return { text: "—", tone: "muted" };
    }
    const delta = roundMoney(desired - current);
    if (Math.abs(delta) < 0.005) {
      return {
        text: formatMoney(delta.toFixed(2)),
        tone: "neutral",
      };
    }
    return {
      text: formatMoney(delta.toFixed(2)),
      tone: delta > 0 ? "positive" : "negative",
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!numericMemberId) return;
    try {
      const res = await applyCorrection({
        member_id: numericMemberId,
        cutover_date: cutoverDate,
        balances: balancesPayload,
      }).unwrap();
      const updated = res?.data?.updated_categories ?? 0;
      toast.success(
        updated > 0
          ? `Saved. ${updated} categor${updated === 1 ? "y" : "ies"} updated.`
          : "No changes — targets already match current balances.",
      );
      onHide?.();
    } catch (err) {
      toast.error(
        err?.data?.message || err?.message || "Could not apply correction.",
      );
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Correct imported opening balance
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontFamily: "'Work Sans', sans-serif" }}>
        <p className="text-secondary small mb-3">
          This is <strong>not</strong> a normal contribution. Enter the{" "}
          <strong>full desired total</strong> staff want for each savings
          category (the system posts the difference as{" "}
          <strong>Balance correction (staff)</strong>).{" "}
          <strong>Current category total</strong> includes{" "}
          <strong>all</strong> recorded activity in that category (opening import,
          contributions, and prior corrections) — not only the original import
          amount.
        </p>
        {memberLabel ? (
          <p className="small mb-2 text-[#383838]">
            <strong>Member:</strong> {memberLabel}
          </p>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="form-label small fw-bold text-uppercase text-muted">
              Cutover date (YYYY-MM-DD)
            </label>
            <input
              type="date"
              className="form-control"
              required
              value={cutoverDate}
              onChange={(e) => setCutoverDate(e.target.value)}
            />
          </div>

          {formLoading ? (
            <div className="py-5 text-center">
              <Spinner animation="border" role="status" />
            </div>
          ) : categories.length === 0 ? (
            <p className="text-muted mb-0">
              No active savings categories for this branch.
            </p>
          ) : (
            <div className="table-responsive rounded-3 border">
              <table
                className="table table-sm mb-0 align-middle"
                style={{ tableLayout: "fixed", width: "100%" }}
              >
                <colgroup>
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "20%" }} />
                </colgroup>
                <thead style={{ background: "var(--color-primary-dark)", color: "#fff" }}>
                  <tr>
                    <th className="small">Category</th>
                    <th className="small">Opening import total</th>
                    <th className="small">Current category total</th>
                    <th className="small">Desired total after correction</th>
                    <th className="small">Adjustment (preview)</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((c) => {
                    const id = c.contribution_category_id;
                    const preview = formatPreviewDelta(c);
                    const openingAt = c.opening_import_at
                      ? moment(c.opening_import_at, "YYYY-MM-DD", true)
                      : null;
                    const previewColor =
                      preview.tone === "positive"
                        ? "#19bc31"
                        : preview.tone === "negative"
                          ? "#ff4343"
                          : preview.tone === "neutral"
                            ? "#525252"
                            : "#a1a0a0";
                    return (
                      <tr key={id}>
                        <td className="small fw-semibold text-break">
                          {c.category_name}
                        </td>
                        <td className="small" style={{ verticalAlign: "middle" }}>
                          <div className="text-break" style={{ lineHeight: 1.25 }}>
                            {formatMoney(
                              Number(c.opening_import_total ?? 0).toFixed(2),
                            )}
                            {openingAt?.isValid() ? (
                              <div
                                className="text-muted fw-normal"
                                style={{ fontSize: 11 }}
                              >
                                Booked {openingAt.format("DD-MMM-YYYY")}
                              </div>
                            ) : null}
                          </div>
                        </td>
                        <td className="small">
                          {formatMoney(Number(c.current_total ?? 0).toFixed(2))}
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm w-100"
                            step="0.01"
                            min="0"
                            value={targets[id] ?? ""}
                            onChange={(e) =>
                              setTargets((prev) => ({
                                ...prev,
                                [id]: e.target.value,
                              }))
                            }
                          />
                        </td>
                        <td
                          className="small fw-semibold text-break"
                          style={{ color: previewColor }}
                        >
                          {preview.text}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="d-flex gap-2 justify-content-end pt-2">
            <button
              type="button"
              className="btn btn-outline-secondary rounded-3 px-4"
              onClick={onHide}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn text-white rounded-3 px-4"
              style={{ background: "var(--color-primary)", borderColor: "var(--color-primary)" }}
              disabled={applying || formLoading || categories.length === 0}
            >
              {applying ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving…
                </>
              ) : (
                "Save correction"
              )}
            </button>
          </div>
        </form>

        <hr className="my-4" />
        <h6
          className="fw-bold text-uppercase small mb-2"
          style={{ color: "#383838" }}
        >
          Recent corrections
        </h6>
        {auditsLoading ? (
          <Spinner size="sm" animation="border" />
        ) : correctionRows.length === 0 ? (
          <p className="small text-muted mb-0">No correction history yet.</p>
        ) : (
          <ul className="list-unstyled small mb-0 space-y-2">
            {correctionRows.map((row) => (
              <li
                key={row.id}
                className="border rounded p-2 mb-2"
                style={{ borderColor: "#e0e0e0" }}
              >
                <div className="fw-semibold">
                  {row.category?.name ?? "Category"}{" "}
                  <span className="text-muted fw-normal">
                    · {row.cutover_date}
                  </span>
                </div>
                <div className="text-muted">
                  {formatMoney(Number(row.previous_total).toFixed(2))} →{" "}
                  {formatMoney(Number(row.new_total).toFixed(2))}
                </div>
                {row.recorded_by ? (
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    By {row.recorded_by.first_name} {row.recorded_by.last_name}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>
    </Modal>
  );
}

function roundMoney(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}
